import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listInboundByOrganization,
  listInboundByStaffRecipient,
} from "../services/emailCorrespondenceApi";
import { resolveEmailPlatformOrganizationGuid } from "../utils/emailPlatformOrganization";
import {
  apiOk,
  buildInboxRows,
  canViewOrgInboundReplies,
  mergeStaffAndOrgInboundRows,
  unreadInboxRows,
} from "../utils/notificationInboxUtils";
import {
  getAllPermissionKeys,
  getSessionRoleName,
  hasPermission,
  isSystemAdministratorRole,
  parsePermissions,
} from "../utils/permissionUtils";

const INBOX_FETCH_TAKE = 200;
const REFRESH_MS = 60_000;

/** Shared unread inbox count for header bell + sidebar notifications menu. */
export default function useInboxUnreadCount(userData) {
  const [rows, setRows] = useState([]);

  const organizationRefId = useMemo(
    () => resolveEmailPlatformOrganizationGuid(userData),
    [userData],
  );

  const staffEmail = useMemo(
    () => String(userData?.EmailId || userData?.emailId || "").trim(),
    [userData?.EmailId, userData?.emailId],
  );

  const isSystemAdmin = isSystemAdministratorRole(getSessionRoleName(userData));
  const hasUserManagement = useMemo(() => {
    const keys = isSystemAdmin
      ? getAllPermissionKeys()
      : parsePermissions(userData?.Permission);
    return hasPermission(keys, "USER_MANAGEMENT");
  }, [isSystemAdmin, userData?.Permission]);

  const canMonitorOrgInbound = canViewOrgInboundReplies({
    isSystemAdmin,
    hasUserManagement,
  });

  const loadInbox = useCallback(async () => {
    if (!organizationRefId) {
      setRows([]);
      return;
    }
    try {
      const [res, orgRes] = await Promise.all([
        listInboundByStaffRecipient({
          organizationRefId,
          take: INBOX_FETCH_TAKE,
        }),
        canMonitorOrgInbound
          ? listInboundByOrganization(organizationRefId, INBOX_FETCH_TAKE)
          : Promise.resolve(null),
      ]);
      if (!apiOk(res)) {
        setRows([]);
        return;
      }
      const staffList = Array.isArray(res.data) ? res.data : [];
      const orgList =
        canMonitorOrgInbound && apiOk(orgRes) && Array.isArray(orgRes.data)
          ? orgRes.data
          : [];
      setRows(
        canMonitorOrgInbound
          ? mergeStaffAndOrgInboundRows(staffList, orgList)
          : staffList,
      );
    } catch {
      setRows([]);
    }
  }, [organizationRefId, canMonitorOrgInbound]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!organizationRefId) return undefined;
    const timer = window.setInterval(loadInbox, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [organizationRefId, loadInbox]);

  const unreadCount = useMemo(
    () =>
      unreadInboxRows(
        buildInboxRows(rows, staffEmail, {
          keepOrgInboundReplies: canMonitorOrgInbound,
        }),
      ).length,
    [rows, staffEmail, canMonitorOrgInbound],
  );
  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  return { unreadCount, badgeLabel, refresh: loadInbox };
}
